import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { absencesApi, employeesApi, ABSENCE_TYPES, LEAVE_TYPES } from "@/api";
import { useLang, fmtDate } from "@/i18n";
import { AbsenceDialog } from "@/components/AbsenceDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { cn } from "@/lib/utils";

const now = new Date();
const YEARS = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];
const TYPE_STYLES = {
  annual_leave: "text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800",
  sick: "text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-800",
  unpaid: "text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700",
  overwork: "text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800",
  overtime: "text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-950/40 border-teal-200 dark:border-teal-800",
};

export const AbsencesTab = () => {
  const { t, lang } = useLang();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [employeeId, setEmployeeId] = useState("all");
  const [type, setType] = useState("all");
  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const monthName = (m) => new Date(2000, m - 1, 1).toLocaleDateString(lang === "el" ? "el-GR" : "en-GB", { month: "long" });

  const load = useCallback(() => {
    absencesApi.list({ year, month: month === "all" ? undefined : month, employee_id: employeeId === "all" ? undefined : employeeId, type }).then(setRows).catch(() => toast.error(t("error")));
    absencesApi.summary(year).then(setSummary);
  }, [year, month, employeeId, type, t]);
  useEffect(load, [load]);
  useEffect(() => { employeesApi.list().then(setEmployees); }, []);

  const remove = async () => {
    try { await absencesApi.remove(deleting.id); toast.success(t("absence_deleted")); load(); }
    catch { toast.error(t("error")); } finally { setDeleting(null); }
  };

  return (
    <div className="space-y-5" data-testid="absences-tab">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-28 font-mono" data-testid="absences-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-40" data-testid="absences-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_year")}</SelectItem>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <SelectItem key={m} value={String(m)}>{monthName(m)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger className="w-52" data-testid="absences-employee-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_employees")}</SelectItem>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-52" data-testid="absences-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_types")}</SelectItem>
              {ABSENCE_TYPES.map((k) => <SelectItem key={k} value={k}>{t(`abs_${k}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-blue-800 hover:bg-blue-700" data-testid="create-absence-button">
          <Plus size={16} className="mr-1" /> {t("new_absence")}
        </Button>
      </div>
      <p className="text-xs text-slate-500">{t("absence_note")}</p>

      <div className="erp-card overflow-hidden" data-testid="leave-balance-card">
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-800"><h3 className="font-heading font-semibold">{t("leave_balance")} {year}</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm erp-table">
            <thead>
              <tr className="text-left">
                <th className="px-4 py-2">{t("employee")}</th>
                <th className="px-4 py-2 text-right">{t("entitlement")}</th>
                <th className="px-4 py-2 text-right">{t("used")}</th>
                <th className="px-4 py-2 text-right">{t("remaining")}</th>
                <th className="px-4 py-2 text-right">{t("sick_days")}</th>
                <th className="px-4 py-2 text-right hidden md:table-cell">{t("unpaid_days")}</th>
                <th className="px-4 py-2 text-right">{t("overtime_hours")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-mono">
              {summary.filter((s) => s.active).map((s) => (
                <tr key={s.employee_id} data-testid={`leave-balance-row-${s.employee_id}`}>
                  <td className="px-4 py-2 font-body font-medium">{s.employee_name}</td>
                  <td className="px-4 py-2 text-right">{s.entitlement}</td>
                  <td className="px-4 py-2 text-right">{s.used}</td>
                  <td className={cn("px-4 py-2 text-right font-semibold", s.remaining < 0 ? "text-rose-600" : "text-emerald-700 dark:text-emerald-300")} data-testid={`leave-remaining-${s.employee_id}`}>{s.remaining}</td>
                  <td className="px-4 py-2 text-right">{s.sick_days}</td>
                  <td className="px-4 py-2 text-right hidden md:table-cell">{s.unpaid_days}</td>
                  <td className="px-4 py-2 text-right">{s.overtime_hours}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="erp-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm erp-table">
            <thead>
              <tr className="text-left">
                <th className="px-4 py-3">{t("employee")}</th>
                <th className="px-4 py-3">{t("type")}</th>
                <th className="px-4 py-3">{t("start_date")}</th>
                <th className="px-4 py-3 hidden md:table-cell">{t("end_date")}</th>
                <th className="px-4 py-3 text-right">{t("days")} / {t("hours")}</th>
                <th className="px-4 py-3 hidden lg:table-cell">{t("notes")}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`absence-row-${a.id}`}>
                  <td className="px-4 py-3 font-medium">{a.employee_name}</td>
                  <td className="px-4 py-3"><span className={cn("inline-flex px-2 py-0.5 rounded border text-xs font-medium", TYPE_STYLES[a.type])}>{t(`abs_${a.type}`)}</span></td>
                  <td className="px-4 py-3 font-mono">{fmtDate(a.date, lang)}</td>
                  <td className="px-4 py-3 font-mono hidden md:table-cell text-slate-500">{LEAVE_TYPES.includes(a.type) ? fmtDate(a.end_date, lang) : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono" data-testid={`absence-amount-${a.id}`}>{LEAVE_TYPES.includes(a.type) ? `${a.days} ${t("days").toLowerCase()}` : `${a.hours} h`}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-slate-500 truncate max-w-xs">{a.notes || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => { setEditing(a); setOpen(true); }} data-testid={`absence-edit-${a.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500"><Pencil size={15} /></button>
                      <button onClick={() => setDeleting(a)} data-testid={`absence-delete-${a.id}`} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-rose-100 dark:hover:bg-rose-950 text-rose-600"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500" data-testid="absences-empty">{t("no_results")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <AbsenceDialog open={open} onOpenChange={setOpen} absence={editing} employees={employees} onSaved={load} />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} message={t("confirm_delete_absence")} onConfirm={remove} />
    </div>
  );
};
